import { useCallback, useMemo, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@store/index";
import { useDebounce } from "@hooks/use-debounce";
import {
  significanceLevelToPValue,
  testTypeToApiFormat,
} from "@utils/statistical-testing-utils";
import {
  getTimePeriodDates,
  resolveTimePeriodInWindow,
} from "@utils/time-period-utils";

const NAMED_PERIOD_VALUES = new Set([
  "latest_quarter",
  "current_quarter",
  "previous_quarter",
  "last_quarter",
  "latest_semester",
  "current_semester",
  "previous_semester",
  "last_semester",
]);
import {
  crosstabConfigSelector,
  crosstabConfigDispatcher,
  QuestionSelection,
  FocusType,
  FocusSelection,
  TimePeriod,
  StatTestType,
  SignificanceLevel,
  type CrosstabConfig,
  updateResponseDates as updateResponseDatesAction,
  initializeRowColumnOrder as initializeRowColumnOrderAction,
} from "@store/slices/crosstab-config-slice";
import {
  chartDrawerSelector,
  chartDrawerDispatcher,
} from "@store/slices/chart-drawer-slice";
import {
  useGetDatasetQuestionVariablesByElementQuery,
  useGetWeightConfigurationsQuery,
  useGetDatasetsQuery,
  useGetDatasetTimeBoundsQuery,
  useGetDatasetByIdQuery,
} from "@store/api/dataset-api";
import { buildDefaultDateGuardrails } from "@models/Dataset";
import { useSearchQuestionResponsesMutation } from "@store/api/filters-api";
import { useCrosstabQuestionSelection } from "./use-crosstab-question-selection";
import { parseDashboardSlug } from "@/utils/slug-utils";
import { questionSessionMemory } from "@/utils/question-session-memory";
import { useCrosstabFilterToggle } from "./use-crosstab-filter-toggle";
import {
  transformCrosstabData,
  extractColumnNValues,
} from "@/utils/crosstab-utils";
import type { VariableData } from "@/models/Filter";
import { showSnackbar } from "@store/slices/snackbar-slice";

// Constants
const DRAWER_CLOSE_DELAY = 100;

/**
 * Helper function to get the correct weight selection value for API requests
 */
const getWeightSelection = (config: CrosstabConfig): string => {
  if (config.weightOption === "default") {
    return "DATASET_DEFAULT";
  }
  if (config.weightOption === "none") {
    return "DISABLED";
  }
  if (config.weightOption === "per-segment") {
    // Fall back to default weight if no per-segment weights are configured yet
    // (e.g. after question change clears stale weights)
    if (
      !config.perSegmentWeights ||
      Object.keys(config.perSegmentWeights).length === 0
    ) {
      return "DATASET_DEFAULT";
    }
    return "PER_SEGMENT_WEIGHT";
  }
  // Custom weight
  if (config.customWeight) {
    return `${config.customWeight.questionId}_${config.customWeight.variableId}`;
  }
  // Fallback to disabled if custom weight is not properly configured
  return "DISABLED";
};

/**
 * Helper function to build crossColumnWeights array from perSegmentWeights
 */
const getCrossColumnWeights = (
  config: CrosstabConfig,
  columnVariableIds: Record<string, string>, // Map of column label -> variable ID
):
  | {
      surveyId: string;
      crossColumnLabel: string;
      weightVariableLabel: string;
    }[]
  | undefined => {
  if (config.weightOption !== "per-segment") {
    return undefined;
  }

  // Check if perSegmentWeights exists and has entries
  if (
    !config.perSegmentWeights ||
    Object.keys(config.perSegmentWeights).length === 0
  ) {
    return undefined;
  }

  const weights = Object.entries(config.perSegmentWeights).map(
    ([columnLabel, weight]) => ({
      surveyId: weight.surveyId,
      // Prefer crossColumnVariableId already stored in the weight config,
      // fall back to runtime columnVariableIds mapping from API response
      crossColumnLabel:
        weight.crossColumnVariableId ||
        columnVariableIds[columnLabel] ||
        columnLabel,
      weightVariableLabel: weight.weightVariableLabel,
    }),
  );

  return weights.length > 0 ? weights : undefined;
};

/**
 * Helper function to add compare-to-total parameters to request body
 * @param requestBody The request body to add parameters to
 * @param compareToTotal Whether compare to total is enabled
 * @param referenceColumnEntity null for "Overall Total", crossVariableId for "Reference Column"
 */
const addCompareToTotalParams = (
  requestBody: any,
  compareToTotal: boolean,
  referenceColumnEntity: string | null,
) => {
  if (compareToTotal) {
    requestBody.enableRowStatTesting = true;
    if (referenceColumnEntity) {
      // Use reference column mode - send the crossVariableId
      requestBody.referenceColumnEntity = referenceColumnEntity;
    } else {
      // Use overall total mode
      requestBody.referenceColumnEntity = null;
    }
  }
};

export const useCrosstabConfigDrawer = () => {
  const { slug } = useParams<{ slug: string }>();
  const { id: slugDashboardId = "" } = parseDashboardSlug(slug || "");

  const dispatch = useAppDispatch();
  const { currentConfig, activeFilters } = useAppSelector(
    crosstabConfigSelector,
  );

  // Prefer dashboardId stored in Redux (set at modal open time, works even when
  // ChartConfigDrawer is rendered outside the /dashboard/:slug route tree).
  // Fall back to URL param for backwards-compatibility.
  const dashboardId = currentConfig.dashboardId || slugDashboardId;
  const { isDrawerOpen, editingWidgetId } = useAppSelector(chartDrawerSelector);

  const { closeChartDrawer } = chartDrawerDispatcher(dispatch);
  const {
    updateChartTitle,
    updateChartSubtitle,
    updateTitleBold,
    updateTitleUnderline,
    updateRowQuestion,
    updateColumnQuestion,
    updateRowFocus,
    updateColumnFocus,
    updatePercentageFormatting,
    updateShowN,
    updateMinimumBaseSize,
    updateWeightOption,
    updateCustomWeight,
    updatePerSegmentWeights,
    updateCrosstabData,
    updateTimePeriod,
    updateTimePeriodDates,
    updateRollingAverageEnabled,
    updateRollingAverageWindowSize,
    updateStatTestingEnabled,
    updateStatTestType,
    updateSignificanceLevel,
    updateMinimumSampleSize,
    updateCompareToTotal,
    updateReferenceColumnEntity,
    saveCurrentConfig,
    resetToDefault,
    toggleRowVisibility,
    toggleColumnVisibility,
    updateCustomRowLabel,
    updateCustomColumnLabel,
    resetCustomRowLabel,
    resetCustomColumnLabel,
    showAllRows,
    showAllColumns,
    reorderRows,
    reorderColumns,
    updateSortingOption,
  } = crosstabConfigDispatcher(dispatch);

  // API mutation for fetching crosstab data
  const [searchQuestionResponses, { isLoading: isFetchingCrosstabData }] =
    useSearchQuestionResponsesMutation();

  // Toggle for using dashboard date filters in preview
  const [usePreviewDateFilter, setUsePreviewDateFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const dfStart = params.get("df_start");
    const dfEnd = params.get("df_end");
    return Boolean(dfStart && dfEnd);
  });

  // Store raw API response data
  const [rawCrosstabData, setRawCrosstabData] = useState<VariableData[]>([]);

  // Extract column variable IDs from crosstab response data
  // The backend provides crossVariableId in descriptiveMetrics
  const columnVariableIds = useMemo(() => {
    const mapping: Record<string, string> = {};

    // Scan ALL variables to capture columns that may not appear in the first row
    rawCrosstabData.forEach((variable) => {
      const descriptiveMetrics = variable?.surveys?.[0]?.descriptiveMetrics;

      if (descriptiveMetrics) {
        descriptiveMetrics.forEach((metric) => {
          if (metric.crossVariableId && metric.crossVariableLabel) {
            mapping[metric.crossVariableLabel] = metric.crossVariableId;
          }
        });
      }
    });

    return mapping;
  }, [rawCrosstabData]);

  // Refs to prevent double fetching
  const isInitialLoad = useRef(false);
  const hasFetchedOnOpen = useRef(false);
  const lastFetchedQuestions = useRef<{
    rowQuestionId: string | null;
    columnQuestionId: string | null;
  }>({ rowQuestionId: null, columnQuestionId: null });

  // CRITICAL: Request deduplication system
  const lastRequestTimestamp = useRef<number>(0);
  const lastRequestParams = useRef<string>("");
  // Ref to always have latest rolling average config without adding to useCallback deps
  const rollingAverageRef = useRef(currentConfig.rollingAverage);
  rollingAverageRef.current = currentConfig.rollingAverage;
  const REQUEST_DEBOUNCE_MS = 100; // Prevent duplicate requests within 100ms

  // Local state for immediate UI updates
  const [localMinimumBaseSize, setLocalMinimumBaseSize] = useState(
    currentConfig.minimumBaseSize,
  );

  // Debounce the local value for API requests
  const debouncedMinimumBaseSize = useDebounce(localMinimumBaseSize, 800);

  // Sync local state when config changes from outside
  useEffect(() => {
    setLocalMinimumBaseSize(currentConfig.minimumBaseSize);
  }, [currentConfig.minimumBaseSize]);

  // Transform data with current decimal formatting and minimum base size
  const crosstabPreviewData = useMemo(() => {
    if (rawCrosstabData.length === 0) return [];
    return transformCrosstabData(
      rawCrosstabData,
      currentConfig.percentageFormatting,
      currentConfig.minimumBaseSize,
    );
  }, [
    rawCrosstabData,
    currentConfig.percentageFormatting,
    currentConfig.minimumBaseSize,
  ]);

  // Initialize row/column order when preview data becomes available
  useEffect(() => {
    if (crosstabPreviewData && crosstabPreviewData.length > 0) {
      const rowIds = crosstabPreviewData.map((row, index) =>
        String(row.id || index),
      );

      // Collect column IDs from ALL rows to handle cases where
      // different rows have different numbers of descriptiveMetrics
      const columnIdSet = new Set<string>();
      crosstabPreviewData.forEach((row) => {
        Object.keys(row).forEach((key) => {
          if (
            key !== "id" &&
            key !== "label" &&
            key !== "_metrics" &&
            key !== "enableStatTesting" &&
            key !== "enableRowStatTesting"
          ) {
            columnIdSet.add(key);
          }
        });
      });
      const columnIds = Array.from(columnIdSet);

      dispatch(initializeRowColumnOrderAction({ rowIds, columnIds }));
    }
  }, [crosstabPreviewData, dispatch]);

  // Extract N values for columns
  const columnNValues = useMemo(() => {
    if (rawCrosstabData.length === 0) return {};
    return extractColumnNValues(rawCrosstabData);
  }, [rawCrosstabData]);

  // Check if any visible cells are suppressed (either by API or due to minimum base size)
  const hasSuppressedCells = useMemo(() => {
    if (rawCrosstabData.length === 0) return false;

    return rawCrosstabData.some((variable, index) => {
      // Skip hidden rows
      const rowId = String(index + 1);
      if (currentConfig.hiddenRows?.includes(rowId)) return false;

      const descriptiveMetrics = variable.surveys[0]?.descriptiveMetrics;
      return descriptiveMetrics?.some(
        (metric) =>
          // Skip hidden columns
          !currentConfig.hiddenColumns?.includes(metric.crossVariableLabel) &&
          (metric.suppressed || metric.baseN < currentConfig.minimumBaseSize),
      );
    });
  }, [
    rawCrosstabData,
    currentConfig.minimumBaseSize,
    currentConfig.hiddenRows,
    currentConfig.hiddenColumns,
  ]);

  // Fetch weight configurations
  const { data: weightConfigs } = useGetWeightConfigurationsQuery(
    currentConfig.datasetId || "",
    {
      skip: !currentConfig.datasetId,
    },
  );

  const { data: datasets = [], isFetching: datasetsFetching } =
    useGetDatasetsQuery({});
  const { data: datasetDetails } = useGetDatasetByIdQuery(
    currentConfig.datasetId as string,
    { skip: !currentConfig.datasetId },
  );
  const pitGuardrails = useMemo(() => {
    return (
      datasetDetails?.dateGuardrails?.pointInTime ??
      buildDefaultDateGuardrails().pointInTime
    );
  }, [datasetDetails?.dateGuardrails]);
  const currentDataset = useMemo(
    () => datasets.find((d) => d.id === currentConfig.datasetId),
    [datasets, currentConfig.datasetId],
  );
  const isMultiSurveyDataset = useMemo(() => {
    const surveyCount = currentDataset?.surveyIds?.length ?? 0;
    if (surveyCount <= 1) return false;

    const configs = weightConfigs?.weightConfigurations ?? [];
    if (configs.length !== surveyCount) return true;

    const first = configs[0];
    return !configs.every(
      (c) =>
        c.datasetElementQuestionId === first.datasetElementQuestionId &&
        c.variableLabel === first.variableLabel,
    );
  }, [currentDataset, weightConfigs]);
  const { data: datasetTimeBounds, isFetching: timeBoundsFetching } =
    useGetDatasetTimeBoundsQuery(currentConfig.datasetId as string, {
      skip: !currentConfig.datasetId,
    });
  const dataAvailabilityCutoff =
    currentDataset?.dataAvailabilityDate || datasetTimeBounds?.maxDate;
  const cutoffSettled = !datasetsFetching && !timeBoundsFetching;

  // Get dashboard date filters from URL if toggle is enabled.
  // Resolves named time periods within the dashboard filter window.
  const getDashboardDateFilters = useCallback(() => {
    if (!usePreviewDateFilter) return undefined;

    const params = new URLSearchParams(window.location.search);
    const dfStart = params.get("df_start");
    const dfEnd = params.get("df_end");

    if (!dfStart || !dfEnd) return undefined;

    const timePeriod = currentConfig.timePeriodSelection?.timePeriod;
    const isNamedPeriod = timePeriod && NAMED_PERIOD_VALUES.has(timePeriod);

    if (isNamedPeriod) {
      const effectiveEnd =
        dataAvailabilityCutoff && dataAvailabilityCutoff < dfEnd
          ? dataAvailabilityCutoff
          : dfEnd;
      const resolved = resolveTimePeriodInWindow(
        timePeriod,
        dfStart,
        effectiveEnd,
        datasetTimeBounds?.minDate,
      );
      return resolved
        ? { startDate: dfStart, endDate: effectiveEnd }
        : { startDate: null as null, endDate: null as null };
    }

    return { startDate: dfStart, endDate: dfEnd };
  }, [
    usePreviewDateFilter,
    currentConfig.timePeriodSelection?.timePeriod,
    dataAvailabilityCutoff,
    datasetTimeBounds?.minDate,
  ]);

  // Helper function to check if variables are needed
  const needsVariables = useCallback(
    (question: QuestionSelection | null) =>
      question?.questionType === "Single" && question.variableCount > 1,
    [],
  );

  // Fetch variables for row question if needed
  const { data: rowVariables } = useGetDatasetQuestionVariablesByElementQuery(
    {
      id: currentConfig.datasetId || "",
      dataset_element_id: currentConfig.rowQuestion?.questionId || "",
    },
    {
      skip:
        !currentConfig.datasetId ||
        !currentConfig.rowQuestion ||
        !needsVariables(currentConfig.rowQuestion),
    },
  );

  // Fetch variables for column question if needed
  const { data: columnVariables } =
    useGetDatasetQuestionVariablesByElementQuery(
      {
        id: currentConfig.datasetId || "",
        dataset_element_id: currentConfig.columnQuestion?.questionId || "",
      },
      {
        skip:
          !currentConfig.datasetId ||
          !currentConfig.columnQuestion ||
          !needsVariables(currentConfig.columnQuestion),
      },
    );

  // Question selection hooks
  const rowQuestionSelection = useCrosstabQuestionSelection({
    datasetId: currentConfig.datasetId,
    questionType: "row",
    dashboardId,
  });

  const columnQuestionSelection = useCrosstabQuestionSelection({
    datasetId: currentConfig.datasetId,
    questionType: "column",
    dashboardId,
  });

  // Auto-select row/column questions from session memory when opening a new crosstab.
  // Fires once when questions are loaded and no selection exists yet.
  // Row  → most recent from "row" slot (or "general" fallback)
  // Column → second-most recent (skipping whatever was auto-selected for row)
  useEffect(() => {
    const isNewWidget = editingWidgetId === "new-widget";
    if (!isNewWidget || !isDrawerOpen) return;
    if (currentConfig.rowQuestion && currentConfig.columnQuestion) return; // both already set

    const { recentQuestionIds, questions } = rowQuestionSelection;

    // Determine the row candidate — either already selected or picked from recent history.
    let rowCandidate = currentConfig.rowQuestion
      ? (rowQuestionSelection.questions.find(
          (q) => q.datasetElementId === currentConfig.rowQuestion!.questionId,
        ) ?? null)
      : null;

    if (!rowCandidate) {
      if (recentQuestionIds.length === 0 || questions.length === 0) return;
      rowCandidate =
        recentQuestionIds
          .map((id) => questions.find((q) => q.datasetElementId === id))
          .find(Boolean) ?? null;
      if (!rowCandidate) return;
      updateRowQuestion(
        rowQuestionSelection.convertToQuestionSelection(rowCandidate),
      );
    }

    // Skip column auto-select if already set.
    if (currentConfig.columnQuestion) return;

    // Pick the next valid recent question (different from row) as column.
    // Use the column selector's own recentQuestionIds (from "column" slot or "general"
    // fallback) so slot-specific history is respected and FLOAT/NUMERIC questions
    // (excluded from columns) are naturally absent from that list.
    if (columnQuestionSelection.questions.length === 0) return;

    const colCandidate = columnQuestionSelection.recentQuestionIds
      .filter((id) => id !== rowCandidate.datasetElementId)
      .map((id) =>
        columnQuestionSelection.questions.find(
          (q) => q.datasetElementId === id,
        ),
      )
      .find(Boolean);

    if (colCandidate) {
      updateColumnQuestion(
        columnQuestionSelection.convertToQuestionSelection(colCandidate),
      );
    }
  }, [
    editingWidgetId,
    isDrawerOpen,
    rowQuestionSelection.recentQuestionIds,
    rowQuestionSelection.questions,
    columnQuestionSelection.recentQuestionIds,
    columnQuestionSelection.questions,
    currentConfig.rowQuestion,
    currentConfig.columnQuestion,
    updateRowQuestion,
    updateColumnQuestion,
  ]);

  // Weight options
  const weightOptions = useMemo(() => {
    const options = [
      { value: "default", label: "Use Default Weight" },
      { value: "per-segment", label: "Per-segment Weighting" },
      { value: "none", label: "Disabled" },
    ];

    // weightConfigs?.weightConfigurations?.forEach((config) => {
    //   options.push({
    //     value: `custom_${config.datasetElementQuestionId}_${config.datasetElementVariableId}`,
    //     label: `Custom: ${config.questionLabel} - ${config.variableLabel}`,
    //   });
    // });

    return options;
  }, [weightConfigs]);

  // Check if current time period is "all_time"
  const isAllTimePeriod = useMemo(
    () => currentConfig.timePeriodSelection?.timePeriod === "all_time",
    [currentConfig.timePeriodSelection?.timePeriod],
  );

  // Auto-disable Statistical Testing when switching to "all_time" period
  useEffect(() => {
    if (isAllTimePeriod && currentConfig.statisticalTesting?.enabled) {
      // Just disable the toggle without triggering refetch
      updateStatTestingEnabled(false);
    }
  }, [
    isAllTimePeriod,
    updateStatTestingEnabled,
    currentConfig.statisticalTesting?.enabled,
  ]);

  // Generic focus update handler
  const createFocusUpdateHandler = useCallback(
    (updateFn: (config: FocusSelection) => void) => (focusType: FocusType) => {
      updateFn({
        focusType,
        selectedVariableId: undefined,
        selectedValueId: undefined,
      });
    },
    [],
  );

  // Specific handlers for row variable selection
  const handleRowVariableSelect = useCallback(
    (id: string) => {
      if (currentConfig.rowFocus) {
        const variable = rowVariables?.variables?.find(
          (v) => v.datasetElementId === id,
        );
        updateRowFocus({
          ...currentConfig.rowFocus,
          selectedVariableId: id,
          selectedVariableLabel: variable?.label,
        });
      }
    },
    [currentConfig.rowFocus, rowVariables, updateRowFocus],
  );

  // Specific handlers for row value selection
  const handleRowValueSelect = useCallback(
    (id: string) => {
      if (currentConfig.rowFocus) {
        const value = rowVariables?.values?.find(
          (v) => v.datasetElementId === id,
        );
        updateRowFocus({
          ...currentConfig.rowFocus,
          selectedValueId: id,
          selectedValueValue:
            typeof value?.value === "number"
              ? value.value
              : Number(value?.value),
        });
      }
    },
    [currentConfig.rowFocus, rowVariables, updateRowFocus],
  );

  // Specific handlers for column variable selection
  const handleColumnVariableSelect = useCallback(
    (id: string) => {
      if (currentConfig.columnFocus) {
        const variable = columnVariables?.variables?.find(
          (v) => v.datasetElementId === id,
        );
        updateColumnFocus({
          ...currentConfig.columnFocus,
          selectedVariableId: id,
          selectedVariableLabel: variable?.label,
        });
      }
    },
    [currentConfig.columnFocus, columnVariables, updateColumnFocus],
  );

  // Specific handlers for column value selection
  const handleColumnValueSelect = useCallback(
    (id: string) => {
      if (currentConfig.columnFocus) {
        const value = columnVariables?.values?.find(
          (v) => v.datasetElementId === id,
        );
        updateColumnFocus({
          ...currentConfig.columnFocus,
          selectedValueId: id,
          selectedValueValue:
            typeof value?.value === "number"
              ? value.value
              : Number(value?.value),
        });
      }
    },
    [currentConfig.columnFocus, columnVariables, updateColumnFocus],
  );

  // Computed values - Define BEFORE using them in the filter toggle hook
  const rowNeedsFocus = needsVariables(currentConfig.rowQuestion);
  const columnNeedsFocus = needsVariables(currentConfig.columnQuestion);

  const isFocusValid = useCallback(
    (focus: FocusSelection | null, needsFocus: boolean) => {
      if (!needsFocus) return true;
      if (!focus) return false;
      return focus.focusType === "variable"
        ? !!focus.selectedVariableId
        : !!focus.selectedValueId;
    },
    [],
  );

  // Use the filter toggle custom hook
  const {
    localApplyFilters,
    isTogglingFilters,
    isTogglingFiltersRef,
    handleFilterToggle,
  } = useCrosstabFilterToggle({
    activeFilters,
    currentConfig,
    columnVariableIds,
    rowVariables,
    columnVariables,
    rowNeedsFocus,
    columnNeedsFocus,
    isFocusValid,
    onDataUpdate: setRawCrosstabData,
  });

  // Handlers

  // Wrapper functions for customization actions to match expected signatures
  const handleUpdateRowLabel = useCallback(
    (id: string, label: string) => {
      updateCustomRowLabel({ id, label });
    },
    [updateCustomRowLabel],
  );

  const handleUpdateColumnLabel = useCallback(
    (id: string, label: string) => {
      updateCustomColumnLabel({ id, label });
    },
    [updateCustomColumnLabel],
  );

  const handleSave = useCallback(async () => {
    // Store the current preview state

    // Always fetch unfiltered data for saving
    const requestBody: any = {
      datasetId: currentConfig.datasetId,
      questionId: currentConfig.rowQuestion!.questionId,
      crossQuestionId: currentConfig.columnQuestion!.questionId,
      chartType: "CROSSTAB",
      weightSelection: getWeightSelection(currentConfig),
      crossColumnWeights: getCrossColumnWeights(
        currentConfig,
        columnVariableIds,
      ),
      filters: undefined, // Always save unfiltered data
    };

    // Add focus parameters if needed
    if (rowNeedsFocus && currentConfig.rowFocus) {
      requestBody.focusMode = currentConfig.rowFocus.focusType.toUpperCase();
      if (currentConfig.rowFocus.focusType === "variable") {
        const variable = rowVariables?.variables?.find(
          (v) =>
            v.datasetElementId === currentConfig.rowFocus?.selectedVariableId,
        );
        requestBody.focusEntity = variable?.label;
      } else {
        const value = rowVariables?.values?.find(
          (v) => v.datasetElementId === currentConfig.rowFocus?.selectedValueId,
        );
        requestBody.focusEntity =
          typeof value?.value === "number" ? value.value : Number(value?.value);
      }
    }

    if (columnNeedsFocus && currentConfig.columnFocus) {
      requestBody.crossFocusMode =
        currentConfig.columnFocus.focusType.toUpperCase();
      if (currentConfig.columnFocus.focusType === "variable") {
        const variable = columnVariables?.variables?.find(
          (v) =>
            v.datasetElementId ===
            currentConfig.columnFocus?.selectedVariableId,
        );
        requestBody.crossFocusEntity = variable?.label;
      } else {
        const value = columnVariables?.values?.find(
          (v) =>
            v.datasetElementId === currentConfig.columnFocus?.selectedValueId,
        );
        requestBody.crossFocusEntity =
          typeof value?.value === "number" ? value.value : Number(value?.value);
      }
    }

    if (debouncedMinimumBaseSize > 0) {
      requestBody.cellBaseN = debouncedMinimumBaseSize;
    }

    // Include statistical testing parameters if enabled AND not all_time
    if (
      currentConfig.statisticalTesting?.enabled &&
      currentConfig.timePeriodSelection?.timePeriod !== "all_time"
    ) {
      requestBody.enableStatTesting = true;
      requestBody.statTestingType = testTypeToApiFormat(
        currentConfig.statisticalTesting?.testType,
      );
      requestBody.pValue = significanceLevelToPValue(
        currentConfig.statisticalTesting?.significanceLevel || "90%",
      );
      requestBody.minimumSampleSize =
        currentConfig.statisticalTesting?.minimumSampleSize || 30;

      // Add compare-to-total parameters
      addCompareToTotalParams(
        requestBody,
        currentConfig.statisticalTesting?.compareToTotal || false,
        currentConfig.statisticalTesting?.referenceColumnEntity || null,
      );
    }

    // Time period is independent — always include dates when available
    if (
      currentConfig.timePeriodSelection?.startDate &&
      currentConfig.timePeriodSelection?.endDate
    ) {
      requestBody.startDate = currentConfig.timePeriodSelection.startDate;
      requestBody.endDate = currentConfig.timePeriodSelection.endDate;
    }

    // Include rolling average periodCount when enabled
    if (
      currentConfig.rollingAverage?.enabled &&
      currentConfig.rollingAverage?.windowSize
    ) {
      requestBody.periodCount = currentConfig.rollingAverage.windowSize;
    }

    try {
      // Fetch unfiltered data
      const response = await searchQuestionResponses(requestBody).unwrap();

      if (response.data && Array.isArray(response.data)) {
        // Transform unfiltered data
        const unfilteredData = transformCrosstabData(
          response.data,
          currentConfig.percentageFormatting,
          currentConfig.minimumBaseSize,
        );

        // Extract N values from unfiltered data
        const unfilteredColumnNValues = extractColumnNValues(response.data);

        // CRITICAL: DO NOT update timePeriodSelection with response dates here!
        // The save handler fetches unfiltered data which returns the full dataset date range.
        // We must preserve the user's selected dates in timePeriodSelection.
        // Only update responsesMinDate/responsesMaxDate for reference.

        // Save unfiltered data
        if (unfilteredData.length > 0) {
          // CRITICAL: Extract column keys directly from API response crossVariableLabels
          // This ensures we only get actual data columns, not request parameters
          const columnKeys: string[] = [];
          response.data.forEach((variable: any) => {
            const descriptiveMetrics = variable.surveys[0]?.descriptiveMetrics;
            if (descriptiveMetrics) {
              descriptiveMetrics.forEach((metric: any) => {
                if (!columnKeys.includes(metric.crossVariableLabel)) {
                  columnKeys.push(metric.crossVariableLabel);
                }
              });
            }
          });

          const rows = unfilteredData.map((row, index) => ({
            id: String(row.id || index),
            label: String(row.label || ""),
            type: "variable" as const,
          }));

          const columns = columnKeys.map((key) => ({
            id: key,
            label: key,
            type: "variable" as const,
            n: 0,
          }));

          const cells: Record<
            string,
            {
              value: string | number | null;
              n: number;
              suppressed: boolean;
              statSignificance?: number;
              rowStatSignificance?: number;
              refColumnStatSignificance?: 1 | 0 | -1;
            }
          > = {};
          unfilteredData.forEach((row) => {
            const rowId = String(row.id || row.label);

            columnKeys.forEach((colKey) => {
              const cellKey = `${rowId}_${colKey}`;
              const value = row[colKey];
              const metrics = row._metrics?.[colKey];

              cells[cellKey] = {
                value:
                  value === "-" ||
                  value === "()" ||
                  value === null ||
                  value === undefined
                    ? null
                    : value,
                n: 0,
                suppressed:
                  value === "-" ||
                  value === "()" ||
                  value === null ||
                  value === undefined,
                ...(metrics?.statSignificance !== undefined && {
                  statSignificance: metrics.statSignificance,
                }),
                ...(metrics?.rowStatSignificance !== undefined && {
                  rowStatSignificance: metrics.rowStatSignificance,
                }),
                ...(metrics?.refColumnStatSignificance !== undefined && {
                  refColumnStatSignificance: metrics.refColumnStatSignificance,
                }),
              };
            });
          });

          const dataToSave = {
            rows,
            columns,
            cells,
            nValues: unfilteredColumnNValues,
            hasSuppressedCells: response.data.some((variable: any) => {
              const descriptiveMetrics =
                variable.surveys[0]?.descriptiveMetrics;
              return descriptiveMetrics?.some(
                (metric: any) =>
                  metric.baseN < currentConfig.minimumBaseSize ||
                  metric.suppressed,
              );
            }),
            responsesMinDate: response.responsesMinDate,
            responsesMaxDate: response.responsesMaxDate,
          };

          updateCrosstabData(dataToSave);
        }
      }
    } catch (error) {
      console.error("Error fetching unfiltered data for save:", error);
    }

    // Persist row + column question selections to session memory on save.
    // This is the authoritative write point so the next crosstab opened on
    // this dashboard shows the correct "Recently used" badges.
    if (dashboardId && currentConfig.datasetId) {
      if (currentConfig.rowQuestion) {
        questionSessionMemory.push(dashboardId, {
          questionId: currentConfig.rowQuestion.questionId,
          questionType: currentConfig.rowQuestion.questionType,
          datasetId: currentConfig.datasetId,
          slot: "row",
        });
        // Also write to general slot so other chart types see this as recently used
        questionSessionMemory.push(dashboardId, {
          questionId: currentConfig.rowQuestion.questionId,
          questionType: currentConfig.rowQuestion.questionType,
          datasetId: currentConfig.datasetId,
          slot: "general",
        });
      }
      if (currentConfig.columnQuestion) {
        questionSessionMemory.push(dashboardId, {
          questionId: currentConfig.columnQuestion.questionId,
          questionType: currentConfig.columnQuestion.questionType,
          datasetId: currentConfig.datasetId,
          slot: "column",
        });
      }
    }

    saveCurrentConfig();
    setTimeout(closeChartDrawer, DRAWER_CLOSE_DELAY);
  }, [
    saveCurrentConfig,
    closeChartDrawer,
    updateCrosstabData,
    localApplyFilters,
    activeFilters,
    currentConfig,
    rowNeedsFocus,
    columnNeedsFocus,
    rowVariables,
    columnVariables,
    debouncedMinimumBaseSize,
    searchQuestionResponses,
    columnVariableIds,
    dashboardId,
  ]);

  const handleDiscard = useCallback(() => {
    resetToDefault();
    closeChartDrawer();
  }, [resetToDefault, closeChartDrawer]);

  const handleRowQuestionSelect = useCallback(
    (question: QuestionSelection) => {
      // Clear column selection if same question is selected
      if (currentConfig.columnQuestion?.questionId === question.questionId) {
        updateColumnQuestion(null);
      }
      updateRowQuestion(question);
    },
    [currentConfig.columnQuestion, updateRowQuestion, updateColumnQuestion],
  );

  const handleColumnQuestionSelect = useCallback(
    (question: QuestionSelection) => {
      // Clear row selection if same question is selected
      if (currentConfig.rowQuestion?.questionId === question.questionId) {
        updateRowQuestion(null);
      }
      updateColumnQuestion(question);
    },
    [currentConfig.rowQuestion, updateColumnQuestion, updateRowQuestion],
  );

  const handleWeightChange = useCallback(
    (value: string) => {
      if (value === "default" || value === "none" || value === "per-segment") {
        updateWeightOption(value);
        return;
      }

      if (value.startsWith("custom_")) {
        const [questionId, ...variableIdParts] = value
          .replace("custom_", "")
          .split("_");
        const variableId = variableIdParts.join("_");

        const config = weightConfigs?.weightConfigurations?.find(
          (c) =>
            c.datasetElementQuestionId === questionId &&
            c.datasetElementVariableId === variableId,
        );

        if (config) {
          updateCustomWeight({
            questionId: config.datasetElementQuestionId,
            variableId: config.datasetElementVariableId,
            questionLabel: config.questionLabel,
            variableLabel: config.variableLabel,
          });
        }
      }
    },
    [weightConfigs, updateWeightOption, updateCustomWeight],
  );

  // Helper function to extract focus entity with validation
  const getFocusEntity = useCallback(
    (
      focusConfig: FocusSelection | null,
      needsFocus: boolean,
      variables: any,
      type: "row" | "column",
    ): { focusMode?: string; focusEntity?: string | number } | null => {
      if (!needsFocus || !focusConfig) {
        return {};
      }

      const focusMode = focusConfig.focusType.toUpperCase();

      if (focusConfig.focusType === "variable") {
        // Use stored label if available, otherwise look it up
        const focusEntity =
          focusConfig.selectedVariableLabel ||
          variables?.variables?.find(
            (v: any) => v.datasetElementId === focusConfig.selectedVariableId,
          )?.label;

        // Return null if focus entity is not available (signals to skip request)
        if (!focusEntity) {
          console.log(
            `⏸️ Skipping request: ${type} variable label not available yet`,
          );
          return null;
        }
        return { focusMode, focusEntity };
      } else {
        // Use stored value if available, otherwise look it up
        const focusEntity =
          focusConfig.selectedValueValue ??
          (() => {
            const value = variables?.values?.find(
              (v: any) => v.datasetElementId === focusConfig.selectedValueId,
            );
            return typeof value?.value === "number"
              ? value.value
              : Number(value?.value);
          })();

        // Return null if focus entity is not available or is NaN (signals to skip request)
        if (
          focusEntity === undefined ||
          focusEntity === null ||
          Number.isNaN(focusEntity)
        ) {
          console.log(`⏸️ Skipping request: ${type} value not available yet`);
          return null;
        }
        return { focusMode, focusEntity };
      }
    },
    [],
  );

  // Centralized request function with deduplication
  const makeRequest = useCallback(
    async (requestBody: any, source: string) => {
      // Create a unique key for this request
      const requestKey = JSON.stringify({
        datasetId: requestBody.datasetId,
        questionId: requestBody.questionId,
        crossQuestionId: requestBody.crossQuestionId,
        filters: requestBody.filters,
        cellBaseN: requestBody.cellBaseN,
        focusMode: requestBody.focusMode,
        focusEntity: requestBody.focusEntity,
        crossFocusMode: requestBody.crossFocusMode,
        crossFocusEntity: requestBody.crossFocusEntity,
        startDate: requestBody.startDate,
        endDate: requestBody.endDate,
        enableStatTesting: requestBody.enableStatTesting,
        periodCount: requestBody.periodCount,
      });

      const now = Date.now();
      const timeSinceLastRequest = now - lastRequestTimestamp.current;

      // Check if this is a duplicate request
      if (
        requestKey === lastRequestParams.current &&
        timeSinceLastRequest < REQUEST_DEBOUNCE_MS
      ) {
        return;
      }

      // Update tracking
      lastRequestTimestamp.current = now;
      lastRequestParams.current = requestKey;

      // Include rolling average periodCount when enabled (only if not already set/cleared by caller)
      if (
        requestBody.periodCount === undefined &&
        rollingAverageRef.current?.enabled &&
        rollingAverageRef.current?.windowSize
      ) {
        requestBody.periodCount = rollingAverageRef.current.windowSize;
      }
      // Clean up null marker — don't send periodCount: null to the API
      if (requestBody.periodCount === null) {
        delete requestBody.periodCount;
      }

      try {
        const response = await searchQuestionResponses(requestBody).unwrap();
        console.log(`✅ ${source} - Request completed`);
        if (response.data && Array.isArray(response.data)) {
          setRawCrosstabData(response.data as VariableData[]);

          // Store responsesMinDate and responsesMaxDate if available
          // CRITICAL: DO NOT update timePeriodSelection here!
          // This function is called from multiple places and would overwrite user-selected dates.
          // Only update responsesMinDate/responsesMaxDate for reference.
          // Use updateResponseDatesAction (not updateCrosstabData) to avoid resetting rowOrder/columnOrder.
          // Dispatch directly to avoid unstable dispatcher function references in useCallback deps.
          if (
            response.responsesMinDate ||
            response.responsesMaxDate ||
            response.effectiveRollingPeriodCount !== undefined
          ) {
            dispatch(
              updateResponseDatesAction({
                responsesMinDate: response.responsesMinDate,
                responsesMaxDate: response.responsesMaxDate,
                effectiveRollingPeriodCount:
                  response.effectiveRollingPeriodCount,
                rollingPeriodsWarningMessage:
                  response.rollingPeriodsWarningMessage,
                comparisonRollingLabel: response.comparisonRollingLabel,
                missingRollingPeriodLabels: response.missingRollingPeriodLabels,
              }),
            );
          }
        }
      } catch (error) {
        console.error(`❌ ${source} - Request failed:`, error);
        setRawCrosstabData([]);

        // Check if error is related to invalid weight selection
        // RTK Query error structure: { status, data: { status, message } }
        const errorStatus = (error as any)?.status;
        const errorData = (error as any)?.data;
        const errorMessage =
          errorData?.message || (error as any)?.message || "";

        // Check for 401/Unauthorized in the error message (even if status is 500)
        const isWeightAuthError =
          errorMessage.includes("401") ||
          errorMessage.includes("Unauthorized") ||
          errorStatus === 401;

        // Check for weight configuration errors (400 status with specific messages)
        const isWeightConfigError =
          errorStatus === 400 &&
          (errorMessage.includes("default weights") ||
            errorMessage.includes("cross column weights"));

        const isNonDefaultWeight =
          requestBody.weightSelection &&
          requestBody.weightSelection !== "DISABLED" &&
          requestBody.weightSelection !== "DATASET_DEFAULT";

        if ((isWeightAuthError || isWeightConfigError) && isNonDefaultWeight) {
          // Show toast error message with the actual API error message
          let toastMessage =
            "The selected weight is not valid for this dataset or combination of questions. Please select a different weight option.";

          // If we have a specific error message from the API, use it
          if (errorMessage) {
            toastMessage = errorMessage;
          }

          dispatch(
            showSnackbar({
              message: toastMessage,
              severity: "error",
            }),
          );
        }
      }
    },
    [searchQuestionResponses, dispatch],
  );

  // Handler for preview date filter toggle
  const handlePreviewDateFilterToggle = useCallback(
    async (enabled: boolean) => {
      setUsePreviewDateFilter(enabled);

      if (!currentConfig.rowQuestion || !currentConfig.columnQuestion) return;
      if (!currentConfig.datasetId) return;

      // Resolve dates inline — getDashboardDateFilters would see stale usePreviewDateFilter
      // since state update is async, so compute directly with the new `enabled` value.
      let overrideDates:
        | { startDate: string | null; endDate: string | null }
        | undefined;
      if (enabled) {
        const params = new URLSearchParams(window.location.search);
        const dfStart = params.get("df_start");
        const dfEnd = params.get("df_end");
        if (dfStart && dfEnd) {
          const timePeriod = currentConfig.timePeriodSelection?.timePeriod;
          const isNamedPeriod =
            timePeriod && NAMED_PERIOD_VALUES.has(timePeriod);
          if (isNamedPeriod) {
            const effectiveEnd =
              dataAvailabilityCutoff && dataAvailabilityCutoff < dfEnd
                ? dataAvailabilityCutoff
                : dfEnd;
            const resolved = resolveTimePeriodInWindow(
              timePeriod,
              dfStart,
              effectiveEnd,
              datasetTimeBounds?.minDate,
            );
            overrideDates = resolved
              ? { startDate: dfStart, endDate: effectiveEnd }
              : { startDate: null, endDate: null };
          } else {
            overrideDates = { startDate: dfStart, endDate: dfEnd };
          }
        }
      }

      const requestBody: any = {
        datasetId: currentConfig.datasetId,
        questionId: currentConfig.rowQuestion.questionId,
        crossQuestionId: currentConfig.columnQuestion.questionId,
        chartType: "CROSSTAB",
        weightSelection: getWeightSelection(currentConfig),
        crossColumnWeights: getCrossColumnWeights(
          currentConfig,
          columnVariableIds,
        ),
        filters:
          localApplyFilters && activeFilters.length > 0
            ? activeFilters
            : undefined,
      };

      if (overrideDates?.startDate && overrideDates?.endDate) {
        requestBody.startDate = overrideDates.startDate;
        requestBody.endDate = overrideDates.endDate;
      } else if (
        !overrideDates &&
        currentConfig.timePeriodSelection?.startDate &&
        currentConfig.timePeriodSelection?.endDate
      ) {
        requestBody.startDate = currentConfig.timePeriodSelection.startDate;
        requestBody.endDate = currentConfig.timePeriodSelection.endDate;
      }

      await makeRequest(requestBody, "Preview Date Filter Toggle");
    },
    [
      currentConfig,
      dataAvailabilityCutoff,
      datasetTimeBounds?.minDate,
      columnVariableIds,
      localApplyFilters,
      activeFilters,
      makeRequest,
    ],
  );

  // Handle statistical testing changes with refetch
  const handleStatTestingChange = useCallback(
    async (
      updateFn: () => void,
      newValues?: {
        enabled?: boolean;
        testType?: StatTestType;
        significanceLevel?: SignificanceLevel;
        minimumSampleSize?: number;
        compareToTotal?: boolean;
        referenceColumnEntity?: string | null;
      },
    ) => {
      updateFn();

      // Refetch data with new statistical testing settings
      const shouldFetchData =
        currentConfig.datasetId &&
        currentConfig.rowQuestion &&
        currentConfig.columnQuestion &&
        isFocusValid(currentConfig.rowFocus, rowNeedsFocus) &&
        isFocusValid(currentConfig.columnFocus, columnNeedsFocus);

      if (!shouldFetchData) return;

      // Determine the values to use (new values if provided, otherwise current state)
      const enabled =
        newValues?.enabled !== undefined
          ? newValues.enabled
          : currentConfig.statisticalTesting?.enabled || false;

      const testType = newValues?.testType
        ? newValues.testType
        : currentConfig.statisticalTesting?.testType || "z-test";

      const significanceLevel = newValues?.significanceLevel
        ? newValues.significanceLevel
        : currentConfig.statisticalTesting?.significanceLevel || "90%";

      const minimumSampleSize =
        newValues?.minimumSampleSize !== undefined
          ? newValues.minimumSampleSize
          : currentConfig.statisticalTesting?.minimumSampleSize || 30;

      const compareToTotal =
        newValues?.compareToTotal !== undefined
          ? newValues.compareToTotal
          : currentConfig.statisticalTesting?.compareToTotal || false;

      const referenceColumnEntity =
        newValues?.referenceColumnEntity !== undefined
          ? newValues.referenceColumnEntity
          : currentConfig.statisticalTesting?.referenceColumnEntity || null;

      // Build the base request body
      const baseRequestBody: any = {
        datasetId: currentConfig.datasetId,
        questionId: currentConfig.rowQuestion!.questionId,
        crossQuestionId: currentConfig.columnQuestion!.questionId,
        chartType: "CROSSTAB",
        weightSelection: getWeightSelection(currentConfig),
        crossColumnWeights: getCrossColumnWeights(
          currentConfig,
          columnVariableIds,
        ),
        filters:
          localApplyFilters && activeFilters.length > 0
            ? activeFilters
            : undefined,
      };

      // Add focus parameters using helper
      const rowFocusResult = getFocusEntity(
        currentConfig.rowFocus,
        rowNeedsFocus,
        rowVariables,
        "row",
      );
      if (rowFocusResult === null) return; // Skip request if focus not ready
      if (rowFocusResult.focusMode) {
        baseRequestBody.focusMode = rowFocusResult.focusMode;
        baseRequestBody.focusEntity = rowFocusResult.focusEntity;
      }

      const columnFocusResult = getFocusEntity(
        currentConfig.columnFocus,
        columnNeedsFocus,
        columnVariables,
        "column",
      );
      if (columnFocusResult === null) return; // Skip request if focus not ready
      if (columnFocusResult.focusMode) {
        baseRequestBody.crossFocusMode = columnFocusResult.focusMode;
        baseRequestBody.crossFocusEntity = columnFocusResult.focusEntity;
      }

      if (debouncedMinimumBaseSize > 0) {
        baseRequestBody.cellBaseN = debouncedMinimumBaseSize;
      }

      // When the dashboard date filter preview is active, use the resolved filtered
      // dates so stat testing is computed for the visible period (e.g. Q1 2025 vs
      // Q4 2024) rather than the globally-resolved period (e.g. Q3 2025 vs Q2 2025).
      const dashboardOverride = getDashboardDateFilters();

      // Handle enabling statistical testing
      if (enabled) {
        // CRITICAL: When enabling stat testing, we need dates for the request.
        // Prefer dashboard filter dates when the toggle is active.
        const effectiveStartDate =
          dashboardOverride?.startDate ??
          currentConfig.timePeriodSelection?.startDate ??
          currentConfig.responsesMinDate;
        const effectiveEndDate =
          dashboardOverride?.endDate ??
          currentConfig.timePeriodSelection?.endDate ??
          currentConfig.responsesMaxDate;

        if (!effectiveStartDate || !effectiveEndDate) {
          // We don't have dates at all - this shouldn't happen in normal flow
          // because the main effect should have already fetched data.
          // Make a request without stat testing to get the dates first.
          console.warn(
            "⚠️ No dates available for stat testing. Fetching date range first.",
          );
          try {
            const dateResponse =
              await searchQuestionResponses(baseRequestBody).unwrap();

            if (
              dateResponse.responsesMinDate &&
              dateResponse.responsesMaxDate
            ) {
              // Store the dates in timePeriodSelection
              updateTimePeriodDates({
                startDate: dateResponse.responsesMinDate,
                endDate: dateResponse.responsesMaxDate,
              });

              // Now make the stat testing request with dates
              const requestBody: any = {
                ...baseRequestBody,
                enableStatTesting: true,
                statTestingType: testTypeToApiFormat(testType),
                pValue: significanceLevelToPValue(significanceLevel),
                minimumSampleSize: minimumSampleSize,
                startDate: dateResponse.responsesMinDate,
                endDate: dateResponse.responsesMaxDate,
              };
              // Add compare-to-total parameters
              addCompareToTotalParams(
                requestBody,
                compareToTotal,
                referenceColumnEntity,
              );

              await makeRequest(
                requestBody,
                "Statistical Testing Handler (with fetched dates)",
              );
            }
          } catch (error) {
            console.error(
              "Error fetching date range for stat testing:",
              error,
            );
          }
        } else {
          const requestBody: any = {
            ...baseRequestBody,
            enableStatTesting: true,
            statTestingType: testTypeToApiFormat(testType),
            pValue: significanceLevelToPValue(significanceLevel),
            minimumSampleSize: minimumSampleSize,
            startDate: effectiveStartDate,
            endDate: effectiveEndDate,
          };
          // Add compare-to-total parameters
          addCompareToTotalParams(
            requestBody,
            compareToTotal,
            referenceColumnEntity,
          );

          await makeRequest(requestBody, "Statistical Testing Handler");
        }
      } else {
        // Disabling stat testing — prefer dashboard filter dates when active
        const disableStartDate =
          dashboardOverride?.startDate ??
          currentConfig.timePeriodSelection?.startDate;
        const disableEndDate =
          dashboardOverride?.endDate ??
          currentConfig.timePeriodSelection?.endDate;

        if (disableStartDate && disableEndDate) {
          baseRequestBody.startDate = disableStartDate;
          baseRequestBody.endDate = disableEndDate;
        }
        // Include rolling average periodCount if active
        const ra = rollingAverageRef.current;
        if (ra?.enabled && ra?.windowSize) {
          baseRequestBody.periodCount = ra.windowSize;
        }
        await makeRequest(
          baseRequestBody,
          "Statistical Testing Handler (disabled)",
        );
      }
    },
    [
      currentConfig,
      rowNeedsFocus,
      columnNeedsFocus,
      isFocusValid,
      rowVariables,
      columnVariables,
      debouncedMinimumBaseSize,
      localApplyFilters,
      activeFilters,
      makeRequest,
      getDashboardDateFilters,
    ],
  );

  // Handle time period changes with refetch
  const handleTimePeriodChange = useCallback(
    async (newTimePeriod: TimePeriod) => {
      // Calculate dates based on the selected time period
      let dates: { startDate: string | null; endDate: string | null } = {
        startDate: null,
        endDate: null,
      };

      // Apply guardrail default rolling average for named periods (PRD Module 2)
      if (
        newTimePeriod === "latest_quarter" ||
        newTimePeriod === "previous_quarter"
      ) {
        const g = pitGuardrails?.units.quarter;
        if (g) {
          updateRollingAverageEnabled(g.defaultRollingAverage !== 0);
          updateRollingAverageWindowSize(
            g.defaultRollingAverage !== 0 ? g.defaultRollingAverage : 2,
          );
        } else {
          // If no guardrails, disable rolling average
          updateRollingAverageEnabled(false);
          updateRollingAverageWindowSize(2);
        }
        const calculatedDates = getTimePeriodDates(
          newTimePeriod,
          dataAvailabilityCutoff,
        );
        if (calculatedDates) {
          dates = calculatedDates;
        }
      } else if (
        newTimePeriod === "latest_semester" ||
        newTimePeriod === "previous_semester"
      ) {
        const g = pitGuardrails?.units.semester;
        if (g) {
          updateRollingAverageEnabled(g.defaultRollingAverage !== 0);
          updateRollingAverageWindowSize(
            g.defaultRollingAverage !== 0 ? g.defaultRollingAverage : 2,
          );
        } else {
          // If no guardrails, disable rolling average
          updateRollingAverageEnabled(false);
          updateRollingAverageWindowSize(2);
        }
        const calculatedDates = getTimePeriodDates(
          newTimePeriod,
          dataAvailabilityCutoff,
        );
        if (calculatedDates) {
          dates = calculatedDates;
        }
      } else if (newTimePeriod === "all_time") {
        // All time: no dates, no rolling average, no stat testing
        updateRollingAverageEnabled(false);
        updateStatTestingEnabled(false);
        updateRollingAverageWindowSize(2);
      } else if (newTimePeriod === "custom_period") {
        // Set default dates: start from responsesMinDate, end from dataAvailabilityDate if set
        if (currentConfig.responsesMinDate && currentConfig.responsesMaxDate) {
          dates = {
            startDate: currentConfig.responsesMinDate,
            endDate: dataAvailabilityCutoff || currentConfig.responsesMaxDate,
          };
        }
      } else {
        const calculatedDates = getTimePeriodDates(
          newTimePeriod,
          dataAvailabilityCutoff,
        );
        if (calculatedDates) {
          dates = calculatedDates as any;
        }
      }

      // Update Redux state with new time period and dates
      updateTimePeriod(newTimePeriod);
      updateTimePeriodDates(dates);

      // Don't make request when selecting custom_period - wait for user to select dates
      // Even if dates weren't available, the dates will populate on the next data fetch
      if (newTimePeriod === "custom_period") {
        return;
      }

      // Refetch data with the new dates
      const shouldFetchData =
        currentConfig.datasetId &&
        currentConfig.rowQuestion &&
        currentConfig.columnQuestion &&
        isFocusValid(currentConfig.rowFocus, rowNeedsFocus) &&
        isFocusValid(currentConfig.columnFocus, columnNeedsFocus);

      if (!shouldFetchData) return;

      const shouldIncludeDates = dates.startDate && dates.endDate;

      // Build the request body
      const requestBody: any = {
        datasetId: currentConfig.datasetId,
        questionId: currentConfig.rowQuestion!.questionId,
        crossQuestionId: currentConfig.columnQuestion!.questionId,
        chartType: "CROSSTAB",
        weightSelection: getWeightSelection(currentConfig),
        crossColumnWeights: getCrossColumnWeights(
          currentConfig,
          columnVariableIds,
        ),
        filters:
          localApplyFilters && activeFilters.length > 0
            ? activeFilters
            : undefined,
        // Include date range if both dates are set
        ...(shouldIncludeDates
          ? {
              startDate: dates.startDate!,
              endDate: dates.endDate!,
            }
          : {}),
        // Include statistical testing parameters if enabled AND not all_time
        ...(currentConfig.statisticalTesting?.enabled &&
        newTimePeriod !== "all_time"
          ? {
              enableStatTesting: true,
              statTestingType: testTypeToApiFormat(
                currentConfig.statisticalTesting?.testType,
              ),
              pValue: significanceLevelToPValue(
                currentConfig.statisticalTesting?.significanceLevel || "90%",
              ),
              minimumSampleSize:
                currentConfig.statisticalTesting?.minimumSampleSize || 30,
            }
          : {}),
      };

      // Add focus parameters using helper
      const rowFocusResult = getFocusEntity(
        currentConfig.rowFocus,
        rowNeedsFocus,
        rowVariables,
        "row",
      );
      if (rowFocusResult === null) return; // Skip request if focus not ready
      if (rowFocusResult.focusMode) {
        requestBody.focusMode = rowFocusResult.focusMode;
        requestBody.focusEntity = rowFocusResult.focusEntity;
      }

      const columnFocusResult = getFocusEntity(
        currentConfig.columnFocus,
        columnNeedsFocus,
        columnVariables,
        "column",
      );
      if (columnFocusResult === null) return; // Skip request if focus not ready
      if (columnFocusResult.focusMode) {
        requestBody.crossFocusMode = columnFocusResult.focusMode;
        requestBody.crossFocusEntity = columnFocusResult.focusEntity;
      }

      if (debouncedMinimumBaseSize > 0) {
        requestBody.cellBaseN = debouncedMinimumBaseSize;
      }

      // Add compare-to-total parameters if stat testing is enabled AND not all_time
      if (
        currentConfig.statisticalTesting?.enabled &&
        newTimePeriod !== "all_time"
      ) {
        addCompareToTotalParams(
          requestBody,
          currentConfig.statisticalTesting?.compareToTotal || false,
          currentConfig.statisticalTesting?.referenceColumnEntity || null,
        );
      }

      // For all_time, explicitly mark periodCount as null so makeRequest won't re-add from stale ref.
      // For other periods with rolling average enabled, explicitly set periodCount: 2 (the reset value)
      // because rollingAverageRef still holds the old windowSize until after re-render.
      if (newTimePeriod === "all_time") {
        requestBody.periodCount = null;
      } else if (rollingAverageRef.current?.enabled) {
        requestBody.periodCount = 2;
      }

      await makeRequest(requestBody, "Time Period Handler");
    },
    [
      updateTimePeriod,
      updateTimePeriodDates,
      updateRollingAverageEnabled,
      updateRollingAverageWindowSize,
      updateStatTestingEnabled,
      pitGuardrails,
      currentConfig,
      rowNeedsFocus,
      columnNeedsFocus,
      isFocusValid,
      rowVariables,
      columnVariables,
      columnVariableIds,
      debouncedMinimumBaseSize,
      localApplyFilters,
      activeFilters,
      makeRequest,
      getFocusEntity,
    ],
  );

  // Handle rolling average changes with refetch
  const handleRollingAverageChange = useCallback(
    async (overridePeriodCount?: number | null) => {
      const shouldFetchData =
        currentConfig.datasetId &&
        currentConfig.rowQuestion &&
        currentConfig.columnQuestion &&
        isFocusValid(currentConfig.rowFocus, rowNeedsFocus) &&
        isFocusValid(currentConfig.columnFocus, columnNeedsFocus);

      if (!shouldFetchData) return;

      const requestBody: any = {
        datasetId: currentConfig.datasetId,
        questionId: currentConfig.rowQuestion!.questionId,
        crossQuestionId: currentConfig.columnQuestion!.questionId,
        chartType: "CROSSTAB",
        weightSelection: getWeightSelection(currentConfig),
        crossColumnWeights: getCrossColumnWeights(
          currentConfig,
          columnVariableIds,
        ),
        filters:
          localApplyFilters && activeFilters.length > 0
            ? activeFilters
            : undefined,
      };

      // Include stat testing params if enabled AND not all_time
      if (
        currentConfig.statisticalTesting?.enabled &&
        currentConfig.timePeriodSelection?.timePeriod !== "all_time"
      ) {
        requestBody.enableStatTesting = true;
        requestBody.statTestingType = testTypeToApiFormat(
          currentConfig.statisticalTesting?.testType,
        );
        requestBody.pValue = significanceLevelToPValue(
          currentConfig.statisticalTesting?.significanceLevel || "90%",
        );
        requestBody.minimumSampleSize =
          currentConfig.statisticalTesting?.minimumSampleSize || 30;

        addCompareToTotalParams(
          requestBody,
          currentConfig.statisticalTesting?.compareToTotal || false,
          currentConfig.statisticalTesting?.referenceColumnEntity || null,
        );
      }

      // Include dates when a specific time period is selected (not all_time)
      const isAllTime =
        currentConfig.timePeriodSelection?.timePeriod === "all_time";
      if (!isAllTime) {
        if (
          currentConfig.timePeriodSelection?.startDate &&
          currentConfig.timePeriodSelection?.endDate
        ) {
          requestBody.startDate = currentConfig.timePeriodSelection.startDate;
          requestBody.endDate = currentConfig.timePeriodSelection.endDate;
        } else if (
          currentConfig.responsesMinDate &&
          currentConfig.responsesMaxDate
        ) {
          requestBody.startDate = currentConfig.responsesMinDate;
          requestBody.endDate = currentConfig.responsesMaxDate;
        }
      }

      // Add focus parameters
      const rowFocusResult = getFocusEntity(
        currentConfig.rowFocus,
        rowNeedsFocus,
        rowVariables,
        "row",
      );
      if (rowFocusResult === null) return;
      if (rowFocusResult.focusMode) {
        requestBody.focusMode = rowFocusResult.focusMode;
        requestBody.focusEntity = rowFocusResult.focusEntity;
      }

      const columnFocusResult = getFocusEntity(
        currentConfig.columnFocus,
        columnNeedsFocus,
        columnVariables,
        "column",
      );
      if (columnFocusResult === null) return;
      if (columnFocusResult.focusMode) {
        requestBody.crossFocusMode = columnFocusResult.focusMode;
        requestBody.crossFocusEntity = columnFocusResult.focusEntity;
      }

      if (debouncedMinimumBaseSize > 0) {
        requestBody.cellBaseN = debouncedMinimumBaseSize;
      }

      // Explicitly set periodCount from override to avoid stale state
      if (overridePeriodCount !== undefined) {
        if (overridePeriodCount !== null) {
          requestBody.periodCount = overridePeriodCount;
        } else {
          // Explicitly mark as disabled so makeRequest won't re-add from stale ref
          requestBody.periodCount = null;
        }
      }

      await makeRequest(requestBody, "Rolling Average Handler");
    },
    [
      currentConfig,
      isFocusValid,
      rowNeedsFocus,
      columnNeedsFocus,
      rowVariables,
      columnVariables,
      columnVariableIds,
      getFocusEntity,
      debouncedMinimumBaseSize,
      localApplyFilters,
      activeFilters,
      makeRequest,
    ],
  );

  // Handle time period dates changes with refetch
  const handleTimePeriodDatesChange = useCallback(
    async (dates: { startDate: string | null; endDate: string | null }) => {
      // Check if dates have actually changed to prevent unnecessary refetches
      const currentStartDate = currentConfig.timePeriodSelection?.startDate;
      const currentEndDate = currentConfig.timePeriodSelection?.endDate;

      const datesChanged =
        currentStartDate !== dates.startDate ||
        currentEndDate !== dates.endDate;

      updateTimePeriodDates(dates);

      // Validate dates: end date must not be earlier than start date
      if (dates.startDate && dates.endDate) {
        const startDate = new Date(dates.startDate);
        const endDate = new Date(dates.endDate);

        if (endDate < startDate) {
          // Don't make request if end date is earlier than start date
          return;
        }
      }

      // Only refetch if dates actually changed AND both dates are set
      const shouldFetchData =
        currentConfig.datasetId &&
        currentConfig.rowQuestion &&
        currentConfig.columnQuestion &&
        isFocusValid(currentConfig.rowFocus, rowNeedsFocus) &&
        isFocusValid(currentConfig.columnFocus, columnNeedsFocus) &&
        datesChanged &&
        dates.startDate &&
        dates.endDate;

      if (!shouldFetchData) return;

      {
        const shouldIncludeDates = dates.startDate && dates.endDate;

        // Build the request body
        const requestBody: any = {
          datasetId: currentConfig.datasetId,
          questionId: currentConfig.rowQuestion!.questionId,
          crossQuestionId: currentConfig.columnQuestion!.questionId,
          chartType: "CROSSTAB",
          weightSelection: getWeightSelection(currentConfig),
          crossColumnWeights: getCrossColumnWeights(
            currentConfig,
            columnVariableIds,
          ),
          filters:
            localApplyFilters && activeFilters.length > 0
              ? activeFilters
              : undefined,
          // Include date range if both dates are set
          ...(shouldIncludeDates
            ? {
                startDate: dates.startDate!,
                endDate: dates.endDate!,
              }
            : {}),
          // Include statistical testing parameters if enabled AND not all_time
          ...(currentConfig.statisticalTesting?.enabled &&
          currentConfig.timePeriodSelection?.timePeriod !== "all_time"
            ? {
                enableStatTesting: true,
                statTestingType: testTypeToApiFormat(
                  currentConfig.statisticalTesting?.testType,
                ),
                pValue: significanceLevelToPValue(
                  currentConfig.statisticalTesting?.significanceLevel || "90%",
                ),
                minimumSampleSize:
                  currentConfig.statisticalTesting?.minimumSampleSize || 30,
              }
            : {}),
        };

        // Add focus parameters using helper
        const rowFocusResult = getFocusEntity(
          currentConfig.rowFocus,
          rowNeedsFocus,
          rowVariables,
          "row",
        );
        if (rowFocusResult === null) return; // Skip request if focus not ready
        if (rowFocusResult.focusMode) {
          requestBody.focusMode = rowFocusResult.focusMode;
          requestBody.focusEntity = rowFocusResult.focusEntity;
        }

        const columnFocusResult = getFocusEntity(
          currentConfig.columnFocus,
          columnNeedsFocus,
          columnVariables,
          "column",
        );
        if (columnFocusResult === null) return; // Skip request if focus not ready
        if (columnFocusResult.focusMode) {
          requestBody.crossFocusMode = columnFocusResult.focusMode;
          requestBody.crossFocusEntity = columnFocusResult.focusEntity;
        }

        if (debouncedMinimumBaseSize > 0) {
          requestBody.cellBaseN = debouncedMinimumBaseSize;
        }

        // Add compare-to-total parameters if stat testing is enabled AND not all_time
        if (
          currentConfig.statisticalTesting?.enabled &&
          currentConfig.timePeriodSelection?.timePeriod !== "all_time"
        ) {
          addCompareToTotalParams(
            requestBody,
            currentConfig.statisticalTesting?.compareToTotal || false,
            currentConfig.statisticalTesting?.referenceColumnEntity || null,
          );
        }

        await makeRequest(requestBody, "Time Period Dates Handler");
      }
    },
    [
      updateTimePeriodDates,
      currentConfig,
      rowNeedsFocus,
      columnNeedsFocus,
      isFocusValid,
      rowVariables,
      columnVariables,
      debouncedMinimumBaseSize,
      localApplyFilters,
      activeFilters,
      makeRequest,
    ],
  );

  // Effect to refetch data when minimum base size changes (after debounce)
  useEffect(() => {
    // Skip if drawer is not open
    if (!isDrawerOpen) {
      return;
    }

    // Skip if we haven't fetched initial data yet
    if (!hasFetchedOnOpen.current) {
      return;
    }

    // Skip if we're toggling filters
    if (isTogglingFiltersRef.current || isTogglingFilters) {
      return;
    }

    const shouldFetchData =
      currentConfig.datasetId &&
      currentConfig.rowQuestion &&
      currentConfig.columnQuestion &&
      isFocusValid(currentConfig.rowFocus, rowNeedsFocus) &&
      isFocusValid(currentConfig.columnFocus, columnNeedsFocus);

    if (!shouldFetchData) {
      return;
    }

    // Build the request body
    const requestBody: any = {
      datasetId: currentConfig.datasetId,
      questionId: currentConfig.rowQuestion!.questionId,
      crossQuestionId: currentConfig.columnQuestion!.questionId,
      chartType: "CROSSTAB",
      weightSelection: getWeightSelection(currentConfig),
      crossColumnWeights: getCrossColumnWeights(
        currentConfig,
        columnVariableIds,
      ),
      filters:
        localApplyFilters && activeFilters.length > 0
          ? activeFilters
          : undefined,
    };

    // Add focus parameters using helper
    const rowFocusResult = getFocusEntity(
      currentConfig.rowFocus,
      rowNeedsFocus,
      rowVariables,
      "row",
    );
    if (rowFocusResult === null) return; // Skip request if focus not ready
    if (rowFocusResult.focusMode) {
      requestBody.focusMode = rowFocusResult.focusMode;
      requestBody.focusEntity = rowFocusResult.focusEntity;
    }

    const columnFocusResult = getFocusEntity(
      currentConfig.columnFocus,
      columnNeedsFocus,
      columnVariables,
      "column",
    );
    if (columnFocusResult === null) return; // Skip request if focus not ready
    if (columnFocusResult.focusMode) {
      requestBody.crossFocusMode = columnFocusResult.focusMode;
      requestBody.crossFocusEntity = columnFocusResult.focusEntity;
    }

    if (debouncedMinimumBaseSize > 0) {
      requestBody.cellBaseN = debouncedMinimumBaseSize;
    }

    // Include statistical testing parameters if enabled AND not all_time
    if (
      currentConfig.statisticalTesting?.enabled &&
      currentConfig.timePeriodSelection?.timePeriod !== "all_time"
    ) {
      requestBody.enableStatTesting = true;
      requestBody.statTestingType = testTypeToApiFormat(
        currentConfig.statisticalTesting?.testType,
      );
      requestBody.pValue = significanceLevelToPValue(
        currentConfig.statisticalTesting?.significanceLevel || "90%",
      );
      requestBody.minimumSampleSize =
        currentConfig.statisticalTesting?.minimumSampleSize || 30;

      // Add compare-to-total parameters
      addCompareToTotalParams(
        requestBody,
        currentConfig.statisticalTesting?.compareToTotal || false,
        currentConfig.statisticalTesting?.referenceColumnEntity || null,
      );
    }

    // Time period is independent — always include dates when available
    if (
      currentConfig.timePeriodSelection?.startDate &&
      currentConfig.timePeriodSelection?.endDate
    ) {
      requestBody.startDate = currentConfig.timePeriodSelection.startDate;
      requestBody.endDate = currentConfig.timePeriodSelection.endDate;
    }

    makeRequest(requestBody, "Minimum Base Size Effect");
  }, [
    debouncedMinimumBaseSize,
    isDrawerOpen,
    currentConfig.datasetId,
    currentConfig.rowQuestion,
    currentConfig.columnQuestion,
    currentConfig.rowFocus,
    currentConfig.columnFocus,
    currentConfig.customWeight,
    currentConfig.perSegmentWeights,
    rowNeedsFocus,
    columnNeedsFocus,
    isFocusValid,
    rowVariables,
    columnVariables,
    localApplyFilters,
    activeFilters,
    makeRequest,
  ]);

  useEffect(() => {
    // Reset flags when drawer closes
    if (!isDrawerOpen) {
      hasFetchedOnOpen.current = false;
      isInitialLoad.current = false;
      lastFetchedQuestions.current = {
        rowQuestionId: null,
        columnQuestionId: null,
      };
      return;
    }

    if (isTogglingFiltersRef.current || isTogglingFilters) {
      return;
    }

    const shouldFetchData =
      currentConfig.datasetId &&
      currentConfig.rowQuestion &&
      currentConfig.columnQuestion &&
      isFocusValid(currentConfig.rowFocus, rowNeedsFocus) &&
      isFocusValid(currentConfig.columnFocus, columnNeedsFocus);

    if (!shouldFetchData) {
      return;
    }

    const questionsChanged =
      lastFetchedQuestions.current.rowQuestionId !==
        currentConfig.rowQuestion?.questionId ||
      lastFetchedQuestions.current.columnQuestionId !==
        currentConfig.columnQuestion?.questionId;

    if (questionsChanged) {
      hasFetchedOnOpen.current = false;
      isInitialLoad.current = false;
    }

    if (hasFetchedOnOpen.current && !questionsChanged) {
      return;
    }

    // For named time periods, wait for availability cutoff queries to settle
    // so we fetch with the correct dates from the start.
    const timePeriod = currentConfig.timePeriodSelection?.timePeriod;
    const isNamedPeriod =
      timePeriod && timePeriod !== "all_time" && timePeriod !== "custom_period";
    if (isNamedPeriod && !cutoffSettled) return;

    hasFetchedOnOpen.current = true;
    isInitialLoad.current = true;

    lastFetchedQuestions.current = {
      rowQuestionId: currentConfig.rowQuestion?.questionId || null,
      columnQuestionId: currentConfig.columnQuestion?.questionId || null,
    };

    const shouldApplyFiltersForInitialLoad =
      activeFilters.length > 0 && localApplyFilters;

    // Resolve correct dates for named periods using the availability cutoff
    let resolvedStartDate = currentConfig.timePeriodSelection?.startDate;
    let resolvedEndDate = currentConfig.timePeriodSelection?.endDate;
    if (isNamedPeriod && timePeriod) {
      const resolved = getTimePeriodDates(timePeriod, dataAvailabilityCutoff);
      if (resolved) {
        resolvedStartDate = resolved.startDate;
        resolvedEndDate = resolved.endDate;
        updateTimePeriodDates(resolved);
      }
    }

    const requestBody: any = {
      datasetId: currentConfig.datasetId,
      questionId: currentConfig.rowQuestion!.questionId,
      crossQuestionId: currentConfig.columnQuestion!.questionId,
      chartType: "CROSSTAB",
      weightSelection: getWeightSelection(currentConfig),
      crossColumnWeights: getCrossColumnWeights(
        currentConfig,
        columnVariableIds,
      ),
      filters: shouldApplyFiltersForInitialLoad ? activeFilters : undefined,
    };

    if (rowNeedsFocus && currentConfig.rowFocus) {
      requestBody.focusMode = currentConfig.rowFocus.focusType.toUpperCase();

      if (currentConfig.rowFocus.focusType === "variable") {
        // Use stored label if available, otherwise look it up
        const focusEntity =
          currentConfig.rowFocus.selectedVariableLabel ||
          rowVariables?.variables?.find(
            (v) =>
              v.datasetElementId === currentConfig.rowFocus?.selectedVariableId,
          )?.label;

        // Skip request if focus entity is not available
        if (!focusEntity) {
          console.log(
            "⏸️ Skipping request: Row variable label not available yet",
          );
          return;
        }
        requestBody.focusEntity = focusEntity;
      } else {
        // Use stored value if available, otherwise look it up
        const focusEntity =
          currentConfig.rowFocus.selectedValueValue ??
          (() => {
            const value = rowVariables?.values?.find(
              (v) =>
                v.datasetElementId === currentConfig.rowFocus?.selectedValueId,
            );
            return typeof value?.value === "number"
              ? value.value
              : Number(value?.value);
          })();

        // Skip request if focus entity is not available or is NaN
        if (
          focusEntity === undefined ||
          focusEntity === null ||
          Number.isNaN(focusEntity)
        ) {
          console.log("⏸️ Skipping request: Row value not available yet");
          return;
        }
        requestBody.focusEntity = focusEntity;
      }
    }

    // Add focus parameters for column question if needed
    if (columnNeedsFocus && currentConfig.columnFocus) {
      requestBody.crossFocusMode =
        currentConfig.columnFocus.focusType.toUpperCase();

      if (currentConfig.columnFocus.focusType === "variable") {
        // Use stored label if available, otherwise look it up
        const focusEntity =
          currentConfig.columnFocus.selectedVariableLabel ||
          columnVariables?.variables?.find(
            (v) =>
              v.datasetElementId ===
              currentConfig.columnFocus?.selectedVariableId,
          )?.label;

        // Skip request if focus entity is not available
        if (!focusEntity) {
          console.log(
            "⏸️ Skipping request: Column variable label not available yet",
          );
          return;
        }
        requestBody.crossFocusEntity = focusEntity;
      } else {
        // Use stored value if available, otherwise look it up
        const focusEntity =
          currentConfig.columnFocus.selectedValueValue ??
          (() => {
            const value = columnVariables?.values?.find(
              (v) =>
                v.datasetElementId ===
                currentConfig.columnFocus?.selectedValueId,
            );
            return typeof value?.value === "number"
              ? value.value
              : Number(value?.value);
          })();

        // Skip request if focus entity is not available or is NaN
        if (
          focusEntity === undefined ||
          focusEntity === null ||
          Number.isNaN(focusEntity)
        ) {
          console.log("⏸️ Skipping request: Column value not available yet");
          return;
        }
        requestBody.crossFocusEntity = focusEntity;
      }
    }

    // Add statistical parameters - use debounced value for API request
    if (debouncedMinimumBaseSize > 0) {
      requestBody.cellBaseN = debouncedMinimumBaseSize;
    }

    // Include statistical testing parameters if enabled AND not all_time
    if (
      currentConfig.statisticalTesting?.enabled &&
      currentConfig.timePeriodSelection?.timePeriod !== "all_time"
    ) {
      requestBody.enableStatTesting = true;
      requestBody.statTestingType = testTypeToApiFormat(
        currentConfig.statisticalTesting?.testType,
      );
      requestBody.pValue = significanceLevelToPValue(
        currentConfig.statisticalTesting?.significanceLevel || "90%",
      );
      requestBody.minimumSampleSize =
        currentConfig.statisticalTesting?.minimumSampleSize || 30;

      // Add compare-to-total parameters
      addCompareToTotalParams(
        requestBody,
        currentConfig.statisticalTesting?.compareToTotal || false,
        currentConfig.statisticalTesting?.referenceColumnEntity || null,
      );
    }

    // Time period is independent — always include dates when available
    if (resolvedStartDate && resolvedEndDate) {
      requestBody.startDate = resolvedStartDate;
      requestBody.endDate = resolvedEndDate;
    }

    makeRequest(requestBody, "Main Fetch Effect");
  }, [
    currentConfig.datasetId,
    currentConfig.rowQuestion,
    currentConfig.columnQuestion,
    currentConfig.rowFocus,
    currentConfig.columnFocus,
    currentConfig.customWeight,
    currentConfig.perSegmentWeights,
    rowNeedsFocus,
    columnNeedsFocus,
    isFocusValid,
    rowVariables,
    columnVariables,
    searchQuestionResponses,
    isDrawerOpen,
    editingWidgetId,
    localApplyFilters,
    cutoffSettled,
  ]);

  const isAvailableData =
    !currentConfig.data || currentConfig.data.rows.length === 0;

  const canSave =
    currentConfig.datasetId &&
    currentConfig.rowQuestion !== null &&
    currentConfig.columnQuestion !== null &&
    isFocusValid(currentConfig.rowFocus, rowNeedsFocus) &&
    isFocusValid(currentConfig.columnFocus, columnNeedsFocus) &&
    !(currentConfig.weightOption === "per-segment" && isMultiSurveyDataset);

  // Handler for reference column selection with auto-unhide logic
  const handleReferenceColumnSelect = useCallback(
    (columnId: string | null) => {
      if (columnId) {
        // columnId is crossVariableId from the dropdown value
        // hiddenColumns stores column labels, so reverse-lookup the label
        const columnLabel = Object.entries(columnVariableIds).find(
          ([, varId]) => varId === columnId,
        )?.[0];
        const labelToCheck = columnLabel || columnId;

        if (currentConfig.hiddenColumns.includes(labelToCheck)) {
          // Auto-unhide the reference column (toggleColumnVisibility expects labels)
          toggleColumnVisibility(labelToCheck);
          // Show snackbar notification
          dispatch(
            showSnackbar({
              message:
                "The reference column was unhidden because it is used for Compare to Total testing.",
              severity: "info",
            }),
          );
        }
      }
      // columnId is already a crossVariableId, pass directly to the store
      updateReferenceColumnEntity(columnId);
    },
    [
      currentConfig.hiddenColumns,
      toggleColumnVisibility,
      updateReferenceColumnEntity,
      columnVariableIds,
      dispatch,
    ],
  );

  // Handler for toggling column visibility with reference column validation
  const handleToggleColumnVisibility = useCallback(
    (columnId: string) => {
      // Check if this column is the current reference column
      // columnId is crossVariableLabel, referenceColumnEntity is crossVariableId
      const columnVariableId = columnVariableIds[columnId] || columnId;
      if (
        currentConfig.statisticalTesting?.compareToTotal &&
        currentConfig.statisticalTesting?.referenceColumnEntity ===
          columnVariableId &&
        !currentConfig.hiddenColumns.includes(columnId) // Only prevent hiding, not unhiding
      ) {
        // Prevent hiding and show message
        dispatch(
          showSnackbar({
            message:
              "The reference column can't be hidden while it is used for Compare to Total testing.",
            severity: "warning",
          }),
        );
        return;
      }
      // Allow toggle
      toggleColumnVisibility(columnId);
    },
    [
      currentConfig.statisticalTesting,
      currentConfig.hiddenColumns,
      toggleColumnVisibility,
      columnVariableIds,
      dispatch,
    ],
  );

  // Wrapper functions for hide/show all that work with actual displayed data
  const handleShowAllRows = useCallback(() => {
    showAllRows();
  }, [showAllRows]);

  const handleHideAllRows = useCallback(() => {
    // Use rowOrder as the source of truth — it's always in sync with the
    // current crosstabPreviewData (updated by initializeRowColumnOrder on
    // every API response), whereas currentConfig.data.rows is only updated
    // on save and may contain stale IDs from a previously-selected question.
    const rowIds =
      currentConfig.rowOrder.length > 0
        ? currentConfig.rowOrder
        : crosstabPreviewData.map((row, index) => String(row.id || index));

    rowIds.forEach((id) => {
      if (!currentConfig.hiddenRows.includes(id)) {
        toggleRowVisibility(id);
      }
    });
  }, [
    currentConfig.rowOrder,
    currentConfig.hiddenRows,
    crosstabPreviewData,
    toggleRowVisibility,
  ]);

  const handleShowAllColumns = useCallback(() => {
    showAllColumns();
  }, [showAllColumns]);

  const handleHideAllColumns = useCallback(() => {
    // Use columnOrder as the source of truth — it's always in sync with the
    // current crosstabPreviewData, whereas currentConfig.data.columns is only
    // updated on save and may contain stale IDs from a previously-selected question.
    const columnIds =
      currentConfig.columnOrder.length > 0
        ? currentConfig.columnOrder
        : (() => {
            const colSet = new Set<string>();
            crosstabPreviewData.forEach((row) => {
              Object.keys(row).forEach((key) => {
                if (
                  key !== "id" &&
                  key !== "label" &&
                  key !== "_metrics" &&
                  key !== "enableStatTesting" &&
                  key !== "enableRowStatTesting"
                ) {
                  colSet.add(key);
                }
              });
            });
            return Array.from(colSet);
          })();

    // Get reference column crossVariableId if applicable
    const referenceColumnVariableId =
      currentConfig.statisticalTesting?.compareToTotal &&
      currentConfig.statisticalTesting?.referenceColumnEntity
        ? currentConfig.statisticalTesting?.referenceColumnEntity
        : null;
    columnIds.forEach((id) => {
      // Skip hiding the reference column (id is crossVariableLabel, referenceColumnVariableId is crossVariableId)
      const idAsVariableId = columnVariableIds[id] || id;
      if (idAsVariableId === referenceColumnVariableId) {
        return;
      }
      if (!currentConfig.hiddenColumns.includes(id)) {
        toggleColumnVisibility(id);
      }
    });

    // Show message if reference column was protected
    if (
      referenceColumnVariableId &&
      columnIds.some(
        (id) => (columnVariableIds[id] || id) === referenceColumnVariableId,
      )
    ) {
      dispatch(
        showSnackbar({
          message:
            "The reference column was not hidden because it is used for Compare to Total testing.",
          severity: "info",
        }),
      );
    }
  }, [
    currentConfig.columnOrder,
    currentConfig.hiddenColumns,
    currentConfig.statisticalTesting,
    columnVariableIds,
    crosstabPreviewData,
    toggleColumnVisibility,
    dispatch,
  ]);

  // Wrapper functions for reordering
  const handleReorderRows = useCallback(
    (fromIndex: number, toIndex: number) => {
      reorderRows({ fromIndex, toIndex });
    },
    [reorderRows],
  );

  const handleReorderColumns = useCallback(
    (fromIndex: number, toIndex: number) => {
      reorderColumns({ fromIndex, toIndex });
    },
    [reorderColumns],
  );

  return {
    // State
    currentConfig,
    isDrawerOpen,
    editingWidgetId,
    weightOptions,
    rowVariables,
    columnVariables,
    rowQuestionSelection,
    columnQuestionSelection,
    activeFilters,
    localApplyFilters,
    isTogglingFilters,
    usePreviewDateFilter,

    // Computed
    isAvailableData,
    rowNeedsFocus,
    columnNeedsFocus,
    canSave,
    isFetchingCrosstabData,
    crosstabPreviewData,
    columnNValues,
    hasSuppressedCells,
    columnVariableIds, // Export column variable IDs for per-segment weighting
    isAllTimePeriod,
    pitGuardrails,

    // Handlers
    handleSave,
    handleDiscard,
    handleRowQuestionSelect,
    handleColumnQuestionSelect,
    handleFilterToggle,
    handlePreviewDateFilterToggle,
    handleRowFocusTypeChange: createFocusUpdateHandler(updateRowFocus),
    handleColumnFocusTypeChange: createFocusUpdateHandler(updateColumnFocus),
    handleRowVariableSelect,
    handleRowValueSelect,
    handleColumnVariableSelect,
    handleColumnValueSelect,
    handleWeightChange,

    // Actions
    closeChartDrawer,
    updateChartTitle,
    updateChartSubtitle,
    updateTitleBold,
    updateTitleUnderline,
    updatePercentageFormatting,
    updateShowN,
    updateMinimumBaseSize,
    updatePerSegmentWeights,
    localMinimumBaseSize,
    setLocalMinimumBaseSize,

    // Time Period Selection actions
    handleTimePeriodChange,
    handleTimePeriodDatesChange,

    // Rolling Average actions
    updateRollingAverageEnabled,
    updateRollingAverageWindowSize,
    handleRollingAverageChange,

    // Statistical Testing actions
    handleStatTestingChange,
    updateStatTestingEnabled,
    updateStatTestType,
    updateSignificanceLevel,
    updateMinimumSampleSize,
    updateCompareToTotal,
    updateReferenceColumnEntity,
    handleReferenceColumnSelect,

    // Row and Column Customization actions
    toggleRowVisibility,
    toggleColumnVisibility: handleToggleColumnVisibility,
    updateCustomRowLabel: handleUpdateRowLabel,
    updateCustomColumnLabel: handleUpdateColumnLabel,
    resetCustomRowLabel,
    resetCustomColumnLabel,
    showAllRows: handleShowAllRows,
    hideAllRows: handleHideAllRows,
    showAllColumns: handleShowAllColumns,
    hideAllColumns: handleHideAllColumns,
    reorderRows: handleReorderRows,
    reorderColumns: handleReorderColumns,
    updateSortingOption,
  };
};
//TODO: fix question get for weight variables
